/**
 * The watch list's values, ported from the design's rwLayer() (L34759) and the rw* keys of
 * renderVals (L38400). Every figure comes from GET /residents/watch/movement; the rules that
 * turn a row into words, colours and a spark are the design's own.
 */
import type { WatchChip, WatchRow } from '@aithora/shared';
import { pre } from './dom';

/** An admission this recent with no usual range yet reads as a new admission. */
export const RECENT_ADMIT_DAYS = 14;

export type WatchSort = 'moved' | 'risk' | 'name' | 'room' | 'stay';
export type WatchView = 'today' | 'shift';

export interface WatchUi {
  sort: WatchSort;
  view: WatchView;
  query: string;
  pick: { key: string; kind: 'n' | 'a' } | null;
  quietOpen: boolean;
  hoOpen: boolean;
  hoSent: string | null;
}

export interface WatchActions {
  setUi: (patch: Partial<WatchUi>) => void;
  open: (residentId: string, tab?: 'overview') => void;
  ack: (residentId: string) => void;
  notice: (residentId: string, what: string) => void;
  assign: (residentId: string, caregiverId: string | null) => void;
  handoff: (lines: string[]) => void;
}

export interface WatchInput {
  rows: WatchRow[];
  /** Care Staff and the Facility Admin act; Aithora and Partner read. */
  canAct: boolean;
  isAdmin: boolean;
  roleLabel: string;
  phone: boolean;
  /** The console's width (the design's shellW). */
  width: number;
  nowMs: number;
  viewerId: string | null;
  lastShiftEnd: string | null;
  caregivers: Array<{ userId: string; name: string | null }>;
  /**
   * Aithora's team and the Owner/Operator read every building: each row names its building
   * (V9 "building name on every row").
   */
  team?: boolean;
  /**
   * The residents who are a live emergency right now (GET /emergency/incidents), and whether
   * someone has claimed each: the row reads "Fall · now · nobody has claimed it yet" (V9).
   */
  emergencies?: Array<{ residentId: string; kind: string; claimed: boolean }>;
  ui: WatchUi;
  act: WatchActions;
}

const RED_T = ['#8F1E17', 'rgba(179,38,30,.12)'];
const GREEN_T = ['#12503C', 'rgba(31,107,82,.13)'];
const MUTE_T = ['#3C454F', 'rgba(34,39,46,.10)'];
const toneOf = (t: string) => (t === 'red' ? RED_T : t === 'green' ? GREEN_T : MUTE_T);

/**
 * "Fall 3h ago": a fall carries its age; what staff noticed reads "Staff: eating less · 4m"
 * (design rwLayer rwNotes); every other chip is its own fixed words.
 */
export function chipLabel(c: WatchChip, nowMs: number): string {
  if (c.kind === 'observation') return 'Staff: ' + c.what.toLowerCase() + ' · ' + Math.max(1, Math.round((nowMs - Date.parse(c.at)) / 60000)) + 'm';
  if (c.kind !== 'fall') return c.what;
  const h = Math.max(1, Math.round((nowMs - Date.parse(c.at)) / 3600000));
  return c.what + ' ' + h + 'h ago';
}

const clockLabel = (iso: string) => new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });

export function watchVals(inp: WatchInput) {
  const { ui, act, canAct, isAdmin, phone, nowMs } = inp;
  const q = ui.query.trim().toLowerCase();
  const pick = ui.pick;
  const lastShift = inp.lastShiftEnd ? Date.parse(inp.lastShiftEnd) : null;

  const rows = inp.rows.map((r, i) => {
    const ser = r.series.map((s) => s.composite);
    const newAdmit = r.isNew;
    // No usual range yet is a new admission only when the admission is recent. A resident
    // in for a year whose score history has just started (a building new to Aithora) was
    // called "New admission, 570 days in".
    const daysIn = r.admittedAt ? Math.max(0, Math.round((nowMs - Date.parse(r.admittedAt)) / 86400000)) : null;
    const recentAdmit = newAdmit && (daysIn === null || daysIn <= RECENT_ADMIT_DAYS);
    const mean = r.baseline ? r.baseline.mean : null;
    const sd = r.baseline ? Math.max(3, r.baseline.sd) : null;
    const last = r.composite;
    const d7 = r.delta ?? 0;
    const z = r.zScore;
    // The live emergency (V9): its row leads with it, in place of the open-alert count.
    const em = (inp.emergencies ?? []).find((x) => x.residentId === r.residentId) ?? null;
    const chips0 = r.chips.map((c) => ({ label: chipLabel(c, nowMs), tone: c.tone, title: c.kind + ' · ' + clockLabel(c.at), at: c.at }));
    const chips = em
      ? [{ label: em.kind + ' · now', tone: 'red', title: 'guardian', at: new Date(nowMs).toISOString() }].concat(chips0.filter((c) => !/open alert/.test(c.label)))
      : chips0;
    const bad = chips.filter((c) => c.tone === 'red' || c.tone === 'amber');
    const good = chips.filter((c) => c.tone === 'green');
    const move = em ? Number.MAX_SAFE_INTEGER : newAdmit ? 0 : r.movementScore ?? 0;
    const zAbs = z == null ? 0 : Math.abs(z);
    const quiet = !em && !newAdmit && zAbs < 1 && bad.length === 0;
    const dirWord = d7 < 0 ? 'down' : 'up';
    const why = em
      ? em.kind + ' · now · ' + (em.claimed ? 'a responder is on the way' : 'nobody has claimed it yet')
      : newAdmit
      ? recentAdmit
        ? 'New admission' + (daysIn !== null ? ', ' + daysIn + ' days in' : '') + '. No history yet, so there is no usual to compare against.'
        : 'No score history yet, so there is no usual to compare against.'
      : bad.length
        ? bad.slice(0, 2).map((c) => c.label.toLowerCase()).join(' + ') + (zAbs >= 1 ? ', wellbeing ' + dirWord + ' vs their usual' : '')
        : zAbs >= 1
          ? 'Wellbeing ' + dirWord + ' ' + Math.abs(d7) + ' in ' + r.windowDays + ' days, outside their usual range'
          : good.length
            ? 'Good movement: ' + good.map((c) => c.label.toLowerCase()).join(', ')
            : 'Within their usual range';
    const mine = !!inp.viewerId && r.assignedTo.some((a) => a.userId === inp.viewerId);
    const owner = r.assignedTo.length ? (mine ? 'You' : r.assignedTo[0].name || 'Assigned') : '';
    const H = 34;
    const pool = ser.concat(mean != null && sd != null ? [mean - sd, mean + sd] : []);
    const lo = (pool.length ? Math.min(...pool) : 0) - 2;
    const hi = (pool.length ? Math.max(...pool) : 100) + 2;
    const Y = (v: number) => (H - 3 - ((v - lo) / Math.max(1, hi - lo)) * (H - 6)).toFixed(1);
    const pts = newAdmit ? ser.slice(-3) : ser;
    const lastV = last ?? (ser.length ? ser[ser.length - 1] : 0);
    const spark = !ser.length && last == null
      ? ''
      : newAdmit
        ? 'M84 ' + Y(lastV) + ' L118 ' + Y(lastV)
        : pts.map((v, k) => (k ? 'L' : 'M') + (2 + k * (116 / Math.max(1, pts.length - 1))).toFixed(1) + ' ' + Y(v)).join(' ');
    const stayDays = r.admittedAt ? Math.max(0, Math.round((nowMs - Date.parse(r.admittedAt)) / 86400000)) : null;
    const masked = !!r.masked;
    const acked = r.acked;
    const btn = 'display:inline-flex;align-items:center;justify-content:center;min-height:' + (phone ? 44 : 34) + 'px;padding:0 11px;border-radius:8px;font-size:16px;font-weight:700;text-decoration:none;white-space:nowrap;';
    const fresh = lastShift != null && r.chips.some((c) => Date.parse(c.at) >= lastShift);
    const ind = r.independence;
    const arrow = ind ? (ind.withHelpTrend === 'down' ? '↓' : ind.withHelpTrend === 'up' ? '↑' : '→') : '';
    return {
      r,
      move,
      quiet,
      newAdmit,
      recentAdmit,
      acked: !!acked,
      stayDays: stayDays ?? 0,
      fresh,
      key: r.residentId,
      name: r.name ?? '–',
      initial: (r.name ?? '').slice(0, 1),
      meta: (masked ? (r.facilityName ?? '–') + ' · initials only' : (inp.team && r.facilityName ? r.facilityName + ' · ' : '') + 'Room ' + (r.room ?? '–') + ' · age ' + (r.age ?? '–')) + ' · ' + (stayDays == null ? '–' : stayDays) + ' days',
      owner: owner || 'Unassigned',
      ownerStyle: 'font-size:14px;font-weight:700;' + (owner ? 'color:#3C454F;' : 'color:#B3261E;'),
      avatar: 'display:flex;align-items:center;justify-content:center;width:34px;height:34px;flex:0 0 34px;border-radius:9px;background:#2F4A6B;color:#fff;font-size:16px;font-weight:700;',
      score: last == null ? '–' : String(last),
      delta: newAdmit ? (recentAdmit ? 'new' : '–') : r.delta == null ? '–' : d7 === 0 ? '→ 0' : (d7 < 0 ? '↓ ' : '↑ ') + Math.abs(d7),
      deltaStyle: 'font-size:16px;font-weight:800;font-variant-numeric:tabular-nums;white-space:nowrap;color:' + (newAdmit ? '#54606C' : d7 <= -3 ? '#B3261E' : d7 >= 3 ? '#1F6B52' : '#54606C') + ';',
      window: newAdmit ? 'no trend yet' : 'over ' + r.windowDays + ' days',
      spark,
      lastY: Y(lastV),
      sparkColor: newAdmit ? '#8B949E' : z != null && z <= -1 ? '#B3261E' : z != null && z >= 1 ? '#1F6B52' : '#2F5C97',
      bandY: mean != null && sd != null ? Y(mean + sd) : '0',
      bandH: mean != null && sd != null ? Math.max(2, Number(Y(mean - sd)) - Number(Y(mean + sd))).toFixed(1) : '0',
      sparkTitle: newAdmit ? 'No history yet' : '14 days · shaded band = their usual range',
      chips: chips.slice(0, 3).map((c) => ({
        label: c.label,
        title: c.title,
        style: 'display:inline-flex;align-items:center;height:22px;padding:0 8px;border-radius:6px;font-size:14px;font-weight:700;white-space:nowrap;color:' + toneOf(c.tone)[0] + ';background:' + toneOf(c.tone)[1] + ';',
      })),
      why: why.charAt(0).toUpperCase() + why.slice(1),
      hasIndep: !newAdmit && !masked && !!ind,
      indepShort: ind ? 'Alone ' + ind.alone + '/' + ind.of + ' this week · ' + ind.withHelp + ' with help ' + arrow : '',
      indep: ind ? 'Independence: did ' + ind.alone + ' of ' + ind.of + ' My day items alone this week, ' + ind.withHelp + ' with help ' + arrow : '',
      ackText: acked ? 'Seen by ' + (acked.by && acked.by === inp.viewerId ? 'you' : r.ackedByName || 'staff') + ' ' + clockLabel(acked.at) : '',
      canAct: canAct && !masked,
      showAck: !acked,
      btnGhost: btn + 'color:#264C7E;border:1px solid rgba(47,92,151,.45);',
      btnSolid: btn + 'color:#fff;background:#2F5C97;',
      assignLabel: isAdmin ? 'Assign ▾' : owner === 'You' ? 'Yours' : 'Assign to me',
      rowStyle:
        'animation:riseIn .26s cubic-bezier(.22,.61,.36,1);animation-delay:' + Math.min(240, i * 22) + 'ms;display:flex;align-items:center;gap:6px 14px;flex-wrap:wrap;min-width:0;padding:8px 8px;border-bottom:1px solid rgba(34,39,46,.09);' +
        (!acked && bad.length ? 'border-left:3px solid ' + (bad.some((c) => c.tone === 'red') ? '#B3261E' : '#6B7580') + ';' : 'border-left:3px solid transparent;') +
        (acked ? 'opacity:.72;' : ''),
      onOpen: (e?: { preventDefault?: () => void }) => { pre(e); act.open(r.residentId); },
      onSbar: (e?: { preventDefault?: () => void }) => { pre(e); act.open(r.residentId, 'overview'); },
      onAck: (e?: { preventDefault?: () => void }) => { pre(e); act.ack(r.residentId); },
      onNotice: (e?: { preventDefault?: () => void }) => {
        pre(e);
        act.setUi({ pick: pick && pick.key === r.residentId && pick.kind === 'n' ? null : { key: r.residentId, kind: 'n' } });
      },
      onAssign: (e?: { preventDefault?: () => void }) => {
        pre(e);
        if (isAdmin) act.setUi({ pick: pick && pick.key === r.residentId && pick.kind === 'a' ? null : { key: r.residentId, kind: 'a' } });
        else if (owner !== 'You') act.assign(r.residentId, null);
      },
      pickOpen: !!(pick && pick.key === r.residentId),
      pickLabel: pick && pick.kind === 'a' ? 'Assign to' : 'What did you notice?',
      picks:
        pick && pick.key === r.residentId
          ? pick.kind === 'a'
            ? inp.caregivers.map((c) => ({ label: c.name ?? 'Caregiver', onClick: (e?: { preventDefault?: () => void }) => { pre(e); act.setUi({ pick: null }); act.assign(r.residentId, c.userId); } }))
            : ['Eating less', 'More confused', 'More tired', 'New pain', 'Not themselves', 'Skin change'].map((w) => ({
                label: w,
                onClick: (e?: { preventDefault?: () => void }) => { pre(e); act.setUi({ pick: null }); act.notice(r.residentId, w.toLowerCase()); },
              }))
          : [],
      sparkWrap: '',
      wideActs: true,
    };
  });

  let shown = rows.filter((x) => !q || x.name.toLowerCase().indexOf(q) >= 0 || String(x.r.room ?? '').indexOf(q) >= 0 || String(x.key).toLowerCase().indexOf(q) >= 0);
  if (ui.view === 'shift') shown = shown.filter((x) => !x.quiet || x.fresh);
  type X = (typeof rows)[number];
  const S: Record<WatchSort, (a: X, b: X) => number> = {
    moved: (a, b) => Number(a.acked) - Number(b.acked) || b.move - a.move,
    name: (a, b) => a.name.localeCompare(b.name),
    room: (a, b) => Number(a.r.room) - Number(b.r.room),
    risk: (a, b) => (b.r.mobilityScore || 0) - (a.r.mobilityScore || 0),
    stay: (a, b) => b.stayDays - a.stayDays,
  };
  shown.sort(S[ui.sort] || S.moved);
  const pw = inp.width;
  const wide = pw >= 1280;
  const SORTS: Array<[WatchSort, string]> = [['moved', 'Most changed'], ['risk', 'Risk'], ['name', 'Name'], ['room', 'Room'], ['stay', 'Stay']];
  const si = Math.max(0, SORTS.findIndex((x) => x[0] === ui.sort));
  const sparkWrap = pw >= 1180 ? 'flex:0 0 120px;min-width:0;' : 'display:none;';
  shown.forEach((x) => {
    x.sparkWrap = sparkWrap;
    x.wideActs = pw >= 1180;
  });
  const active = ui.sort === 'moved' ? shown.filter((x) => !x.quiet) : shown;
  const quietRows = ui.sort === 'moved' ? shown.filter((x) => x.quiet) : [];
  const needs = rows.filter((x) => !x.quiet && !x.acked && !x.newAdmit).length;
  const seg = (on: boolean) =>
    'display:inline-flex;align-items:center;height:' + (phone ? 44 : 32) + 'px;padding:0 12px;font-size:16px;font-weight:700;text-decoration:none;white-space:nowrap;' + (on ? 'background:#22272E;color:#FCFAF4;' : 'color:#3C454F;');
  const changed = rows.filter((x) => !x.quiet).sort((a, b) => b.move - a.move);
  const hoLines = changed.slice(0, 6).map((x) => ({ who: x.name + (x.r.masked ? '' : ' · Rm ' + (x.r.room ?? '–')), what: x.why + (x.owner === 'Unassigned' ? ' · unassigned' : '') }));

  return {
    rwWide: wide,
    rwNarrow: !wide,
    rwHoLabel: pw >= 1100 ? 'Shift handoff' : 'Handoff',
    rwSortLabel: 'Sort: ' + SORTS[si][1] + ' ▾',
    rwSortCycle: (e?: { preventDefault?: () => void }) => { pre(e); act.setUi({ sort: SORTS[(si + 1) % SORTS.length][0] }); },
    rwRows: active.concat(ui.quietOpen ? quietRows : []),
    rwHasQuiet: quietRows.length > 0,
    rwQuietLabel: (ui.quietOpen ? '▾ ' : '▸ ') + quietRows.length + ' resident' + (quietRows.length === 1 ? '' : 's') + ' with nothing unusual',
    rwQuietToggle: (e?: { preventDefault?: () => void }) => { pre(e); act.setUi({ quietOpen: !ui.quietOpen }); },
    rwEmpty: shown.length === 0,
    rwEmptyNote: q ? 'No resident matches “' + q + '”.' : 'Nothing changed since your last shift.',
    rwCounts: [
      { k: 'need a look', v: String(needs), color: needs ? '#B3261E' : '#1F6B52' },
      { k: 'new admissions', v: String(rows.filter((x) => x.recentAdmit).length), color: '#22272E' },
      { k: 'unassigned', v: String(rows.filter((x) => x.owner === 'Unassigned').length), color: '#22272E' },
    ],
    rwLiveDot: 'display:inline-block;width:7px;height:7px;border-radius:50%;background:#1F6B52;animation:pulse 2s ease-in-out infinite;',
    rwViews: ([['today', 'Today'], ['shift', 'Since your last shift']] as Array<[WatchView, string]>).map((v) => ({
      label: v[1],
      style: seg(ui.view === v[0]),
      onClick: (e?: { preventDefault?: () => void }) => { pre(e); act.setUi({ view: v[0] }); },
    })),
    rwSorts: SORTS.map((v) => ({
      label: v[1],
      style:
        'display:inline-flex;align-items:center;height:' + (phone ? 44 : 30) + 'px;padding:0 10px;border-radius:7px;font-size:16px;font-weight:700;text-decoration:none;white-space:nowrap;' +
        (ui.sort === v[0] ? 'color:#264C7E;background:rgba(47,92,151,.12);' : 'color:#54606C;'),
      onClick: (e?: { preventDefault?: () => void }) => { pre(e); act.setUi({ sort: v[0] }); },
    })),
    rwHoOpen: ui.hoOpen,
    rwHoToggle: (e?: { preventDefault?: () => void }) => { pre(e); act.setUi({ hoOpen: !ui.hoOpen }); },
    rwHoMeta: changed.length + ' residents changed · ' + rows.filter((x) => x.acked).length + ' seen',
    rwHoLines: hoLines,
    rwHoSendLabel: ui.hoSent ? '✓ Sent ' + ui.hoSent : 'Send to next nurse',
    rwHoSend: (e?: { preventDefault?: () => void }) => {
      pre(e);
      if (ui.hoSent || !hoLines.length) return;
      act.handoff(hoLines.map((h) => h.who + ' · ' + h.what));
    },
    rwCanAct: canAct,
    rwFoot: 'Ranked by change against each resident’s own usual range, not a fixed threshold. Chips are what changed and when. ' + (canAct ? '' : 'Read-only as ' + inp.roleLabel + '.'),
  };
}
